import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useLayoutEffect,
  useRef,
} from "react";
import * as XLSX from "xlsx";
import { apiService } from "../../services/api";
import { toUTC } from "../../utils/dateUtils";

export interface ReportRawDataLoaderProps {
  /** When true, modal is visible and load query runs (if reportSettings + filterValues provided). */
  isOpen: boolean;
  onClose: () => void;
  /** Display name for the report (e.g. "Purchase register"). */
  reportName?: string;
  /** Report settings id for the API (e.g. "purchase_register"). */
  reportSettings?: string;
  /** Filter values keyed by BindValue (e.g. { txt_start_date: "2024-01-01", txt_end_date: "2024-01-31" }). */
  filterValues?: Record<string, any>;
  /** Optional logged-in user id for branch filtering. */
  loginedUserId?: string;
  /** Current branch id for transaction_branch_id scoping. */
  branchId?: string;
}

/**
 * Ensures date is in DD-MM-YYYY format for API as requested.
 */
function formatDateForReportApi(value: string): string {
  if (!value || typeof value !== "string") return value;
  const trimmed = value.trim();
  
  // Use toUTC to parse safely then format to DD-MM-YYYY
  const iso = toUTC(trimmed);
  if (iso) {
    const d = new Date(iso);
    const pad = (n: number) => n.toString().padStart(2, "0");
    return `${pad(d.getUTCDate())}-${pad(d.getUTCMonth() + 1)}-${d.getUTCFullYear()}`;
  }
  return trimmed;
}

/**
 * Builds the request payload: ReportSettings + formatted filters + optional logined_userid / branch_id.
 */
function buildReportDataPayload(
  reportSettings: string,
  filterValues: Record<string, any>,
  loginedUserId?: string,
  branchId?: string
): Record<string, any> {
  const formattedFilters: Record<string, any> = {};
  for (const [key, value] of Object.entries(filterValues || {})) {
    if (value === null || value === undefined || value === "") continue;
    const str = String(value).trim();
    formattedFilters[key] = /^\d{4}-\d{2}-\d{2}$/.test(str)
      ? formatDateForReportApi(str)
      : str;
  }
  const payload: Record<string, any> = {
    ReportSettings: reportSettings,
    ...formattedFilters,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
  if (loginedUserId) {
    payload.logined_userid = loginedUserId;
  }
  const branch = (branchId || "").trim();
  if (branch) {
    payload.branch_id = branch;
    payload.transaction_branch_id = branch;
  }
  return payload;
}

function normalizeColumnLabel(columnKey: string): string {
  return columnKey.replace(/_/g, " ").trim().toLowerCase();
}

interface DrilldownRule {
  reportSettings: string;
  /** Normalized header labels for the clickable document column (any match). */
  clickColumnLabels: readonly string[];
  /** Report_data / View Data filter key to set when drilling in. */
  filterBindKey: string;
  /** Column labels (normalized) to hide in table / Excel (internal header ids). */
  hiddenColumnNormalizedLabels: readonly string[];
  /** Row object keys to read in order for the API id (first non-empty wins). */
  idRowKeys: readonly string[];
  linkTitle: string;
  /** Shown on the back control when drill-down filter is active. */
  backButtonLabel: string;
}

/** Registers with summary → line drill-down via Raw Data (Parts Issue, Issue Return, Parts Request). */
const DRILLDOWN_RULES: readonly DrilldownRule[] = [
  {
    reportSettings: "parts_issue_register",
    clickColumnLabels: ["issue no", "issue number"],
    filterBindKey: "issue_id",
    hiddenColumnNormalizedLabels: ["issue hdr id"],
    idRowKeys: [
      "ISSUE_HDR_ID",
      "issue_hdr_id",
      "Issue_Hdr_Id",
      "issue_id",
      "ISSUE_ID",
    ],
    linkTitle: "View lines for this issue",
    backButtonLabel: "Back",
  },
  {
    reportSettings: "issue_return_register",
    clickColumnLabels: ["return no", "return number"],
    filterBindKey: "return_id",
    hiddenColumnNormalizedLabels: ["return hdr id"],
    idRowKeys: [
      "RETURN_HDR_ID",
      "return_hdr_id",
      "Return_Hdr_Id",
      "return_id",
      "RETURN_ID",
    ],
    linkTitle: "View lines for this return",
    backButtonLabel: "Back",
  },
  {
    reportSettings: "parts_request_register",
    clickColumnLabels: [
      "request no",
      "parts request no",
      "request number",
      "parts request number",
    ],
    filterBindKey: "parts_request_id",
    hiddenColumnNormalizedLabels: ["parts request id"],
    idRowKeys: ["PARTS_REQUEST_ID", "parts_request_id", "Parts_Request_Id"],
    linkTitle: "View lines for this request",
    backButtonLabel: "Back",
  },
];

function getDrilldownRule(
  reportSettings: string | undefined
): DrilldownRule | undefined {
  if (!reportSettings) return undefined;
  return DRILLDOWN_RULES.find((r) => r.reportSettings === reportSettings);
}

function DrilldownBackButton({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/30 hover:bg-primary-100 dark:hover:bg-primary-900/50 border border-primary-200 dark:border-primary-700 rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
    >
      <svg
        className="w-4 h-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M15 19l-7-7 7-7"
        />
      </svg>
      {label}
    </button>
  );
}

/** Line-level register rows (parts / lines); drill-down on document number is disabled. */
function hasPartNameColumn(columnKeys: string[]): boolean {
  return columnKeys.some(
    (key) => normalizeColumnLabel(key) === "part name"
  );
}

function isHiddenDrilldownColumn(
  columnKey: string,
  rule: DrilldownRule | undefined
): boolean {
  if (!rule) return false;
  const n = normalizeColumnLabel(columnKey);
  return rule.hiddenColumnNormalizedLabels.includes(n);
}

function resolveDrilldownApiValue(
  row: Record<string, any>,
  cellValue: unknown,
  rule: DrilldownRule
): string | null {
  for (const k of rule.idRowKeys) {
    const x = row[k];
    if (x === null || x === undefined) continue;
    const s = String(x).trim();
    if (s !== "") return s;
  }
  if (cellValue !== null && cellValue !== undefined) {
    const s = String(cellValue).trim();
    if (s !== "") return s;
  }
  return null;
}

/** Stable empty array so hooks depending on `dataArray` do not see a new [] each render. */
const EMPTY_REPORT_ROWS: any[] = [];

interface ReportRawSection {
  id: string;
  title: string;
  totalcount: number;
  data: Record<string, unknown>[];
}

export const ReportRawDataLoader: React.FC<ReportRawDataLoaderProps> = ({
  isOpen,
  onClose,
  reportName = "Report",
  reportSettings,
  filterValues = {},
  loginedUserId,
  branchId,
}) => {
  const [rawData, setRawData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [filterText, setFilterText] = useState("");
  const [filterColumn, setFilterColumn] = useState<string>("all");
  const [activeSectionIndex, setActiveSectionIndex] = useState(0);
  /** Filters sent to report_data (same shape as View Data); null until modal syncs from props. */
  const [queryFilters, setQueryFilters] = useState<Record<string, any> | null>(
    null
  );
  const prevIsOpenRef = useRef(false);

  useLayoutEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      setQueryFilters({ ...(filterValues || {}) });
    }
    if (!isOpen) {
      setQueryFilters(null);
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, filterValues]);

  const loadQuery = useCallback(async () => {
    if (!reportSettings || queryFilters === null) return;
    setIsLoading(true);
    setRawData(null);
    try {
      const payload = buildReportDataPayload(
        reportSettings,
        queryFilters,
        loginedUserId,
        branchId
      );
      const response = await apiService.request({
        endpoint: "/pabreport/report_data",
        method: "POST",
        params: { name: reportSettings },
        payload,
        includeAuthToken: true,
        responseType: "json",
      });
      if (response && typeof response === "object") {
        setRawData(response);
      } else {
        throw new Error("Unexpected response format from report_data endpoint");
      }
    } catch (error) {
      console.error("ReportRawDataLoader: failed to load", error);
      setRawData({
        success: false,
        error:
          error instanceof Error ? error.message : "Failed to fetch raw data",
      });
    } finally {
      setIsLoading(false);
    }
  }, [reportSettings, queryFilters, loginedUserId, branchId]);

  // Run load query when modal opens or filters change (e.g. register drill-down)
  useEffect(() => {
    if (isOpen && reportSettings && queryFilters !== null) {
      loadQuery();
    }
  }, [isOpen, reportSettings, queryFilters, loadQuery]);

  // Reset when modal closes
  useEffect(() => {
    if (!isOpen) {
      setRawData(null);
      setFilterText("");
      setActiveSectionIndex(0);
    }
  }, [isOpen]);

  const reportSections: ReportRawSection[] = useMemo(() => {
    if (!rawData?.multi_section || !Array.isArray(rawData.sections)) {
      return [];
    }
    return rawData.sections as ReportRawSection[];
  }, [rawData]);

  const isMultiSection = reportSections.length > 0;

  useEffect(() => {
    if (activeSectionIndex >= reportSections.length) {
      setActiveSectionIndex(0);
    }
  }, [activeSectionIndex, reportSections.length]);

  const activeSection = isMultiSection
    ? reportSections[activeSectionIndex] ?? reportSections[0]
    : null;

  // Derive data and columns unconditionally (no hooks after this)
  const dataArray: any[] = isMultiSection
    ? Array.isArray(activeSection?.data)
      ? activeSection.data
      : EMPTY_REPORT_ROWS
    : Array.isArray(rawData?.data)
      ? rawData.data
      : EMPTY_REPORT_ROWS;
  const totalCount = isMultiSection
    ? activeSection?.totalcount ?? 0
    : rawData?.totalcount || 0;
  const headerData = rawData?.header || [];
  const hasError =
    rawData?.error || (rawData && rawData.success === false);

  const handleDrilldownCellClick = (
    row: Record<string, any>,
    cellValue: unknown,
    rule: DrilldownRule
  ) => {
    const id = resolveDrilldownApiValue(row, cellValue, rule);
    if (!id) return;
    setQueryFilters((prev) => ({
      ...(prev ?? {}),
      [rule.filterBindKey]: id,
    }));
  };

  const rowSchemaKeys = useMemo(
    () => (dataArray.length > 0 ? Object.keys(dataArray[0]) : []),
    [dataArray]
  );

  const activeDrilldownRule = useMemo(
    () => getDrilldownRule(reportSettings),
    [reportSettings]
  );

  const visibleColumns = useMemo(
    () =>
      rowSchemaKeys.filter((c) => !isHiddenDrilldownColumn(c, activeDrilldownRule)),
    [rowSchemaKeys, activeDrilldownRule]
  );

  const drilldownDocumentFilterActive = useMemo(() => {
    if (!activeDrilldownRule || queryFilters === null) return false;
    const v = queryFilters[activeDrilldownRule.filterBindKey];
    return v !== undefined && v !== null && String(v).trim() !== "";
  }, [activeDrilldownRule, queryFilters]);

  const clearDrilldownFilter = useCallback(() => {
    if (!activeDrilldownRule || queryFilters === null) return;
    const key = activeDrilldownRule.filterBindKey;
    setQueryFilters((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, [activeDrilldownRule, queryFilters]);

  const summaryDrilldownActive =
    !!activeDrilldownRule && !hasPartNameColumn(rowSchemaKeys);

  // Front-end filter: must run unconditionally to keep hook order consistent
  const filteredData = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    if (!q) return dataArray;
    return dataArray.filter((row: Record<string, any>) => {
      if (filterColumn && filterColumn !== "all") {
        const val = row[filterColumn];
        const str =
          val !== null && val !== undefined ? String(val).toLowerCase() : "";
        return str.includes(q);
      }

      // Search visible columns only (internal ids excluded)
      return visibleColumns.some((col) => {
        const val = row[col];
        const str =
          val !== null && val !== undefined ? String(val).toLowerCase() : "";
        return str.includes(q);
      });
    });
  }, [dataArray, visibleColumns, filterText, filterColumn]);

  useEffect(() => {
    if (filterColumn !== "all" && !visibleColumns.includes(filterColumn)) {
      setFilterColumn("all");
    }
  }, [filterColumn, visibleColumns]);

  useEffect(() => {
    setFilterText("");
    setFilterColumn("all");
  }, [activeSectionIndex]);

  const overallTotalCount = isMultiSection
    ? reportSections.reduce((sum, section) => sum + (section.totalcount || 0), 0)
    : totalCount;

  const hasAnySectionData = isMultiSection
    ? reportSections.some((section) => (section.data?.length ?? 0) > 0)
    : dataArray.length > 0;

  if (!isOpen) return null;

  const handleDownloadJson = () => {
    const payload = {
      ...rawData,
      data: filteredData,
      totalcount: filteredData.length,
    };
    const jsonString = JSON.stringify(payload, null, 2);
    const blob = new Blob([jsonString], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${reportName.replace(/\s+/g, "_")}_raw_data_${new Date().toISOString().split("T")[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownloadExcel = () => {
    const wb = XLSX.utils.book_new();

    if (isMultiSection) {
      reportSections.forEach((section, index) => {
        const rows = Array.isArray(section.data) ? section.data : [];
        const columns =
          rows.length > 0 ? Object.keys(rows[0]) : [];
        const exportRows = rows.map((row: Record<string, any>) => {
          const o: Record<string, unknown> = {};
          for (const c of columns) {
            o[c] = row[c];
          }
          return o;
        });
        const sheetName = `${index + 1}. ${section.title}`.slice(0, 31);
        const ws =
          columns.length > 0
            ? XLSX.utils.json_to_sheet(exportRows, {
                header: columns,
                skipHeader: false,
              })
            : XLSX.utils.aoa_to_sheet([["No transactions on this date"]]);
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
      });
    } else {
      const exportRows = filteredData.map((row: Record<string, any>) => {
        const o: Record<string, unknown> = {};
        for (const c of visibleColumns) {
          o[c] = row[c];
        }
        return o;
      });
      const ws = XLSX.utils.json_to_sheet(exportRows, {
        header: visibleColumns,
        skipHeader: false,
      });
      XLSX.utils.book_append_sheet(wb, ws, "Data");
    }

    const filename = `${reportName.replace(/\s+/g, "_")}_${new Date().toISOString().split("T")[0]}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  return (
    <div className="fixed  inset-0 z-[999] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-7xl mx-4 max-h-[90vh] overflow-hidden animate-fade-in flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0 flex-wrap">
            {drilldownDocumentFilterActive && activeDrilldownRule && (
              <DrilldownBackButton
                label={activeDrilldownRule.backButtonLabel}
                onClick={clearDrilldownFilter}
              />
            )}
            <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Raw Data - {reportName}
            </h2>
            {isMultiSection && activeSection && (
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mt-1">
                {activeSection.title}
              </p>
            )}
            {(totalCount > 0 || isMultiSection) && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {filterText.trim()
                  ? `Showing ${filteredData.length} of ${totalCount} records`
                  : isMultiSection
                    ? totalCount > 0
                      ? `Section records: ${totalCount} · All sections: ${overallTotalCount}`
                      : `No transactions in this section · All sections: ${overallTotalCount}`
                    : `Total Records: ${totalCount}`}
              </p>
            )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Section selector + front-end filter */}
        <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex-shrink-0 flex flex-wrap items-center gap-3">
          {isMultiSection && (
            <>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Section:
              </label>
              <select
                value={activeSectionIndex}
                onChange={(e) => setActiveSectionIndex(Number(e.target.value))}
                className="min-w-[220px] px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors"
              >
                {reportSections.map((section, index) => (
                  <option key={section.id} value={index}>
                    {section.title} ({section.totalcount})
                  </option>
                ))}
              </select>
            </>
          )}
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Filter:
          </label>
          <select
            value={filterColumn}
            onChange={(e) => setFilterColumn(e.target.value)}
            className="w-48 lowercase px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors"
          >
            <option value="all">All columns</option>
            {visibleColumns.map((col) => (
              <option key={col} value={col}>
                {col.replace(/_/g, " ")}
              </option>
            ))}
          </select>
          <input
            type="text"
            placeholder={filterColumn === "all" ? "Search in all columns..." : `Search in ${filterColumn.replace(/_/g, " ")}...`}
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            className="flex-1 max-w-md px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors"
          />
        </div>

        <div className="flex-1 overflow-hidden flex flex-col">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="inline-block w-8 h-8 border-4 border-gray-200 dark:border-gray-700 border-t-primary-600 dark:border-t-primary-400 rounded-full animate-spin" />
                <p className="mt-4 text-gray-600 dark:text-gray-400">
                  Loading raw data...
                </p>
              </div>
            </div>
          ) : hasError ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="text-red-600 dark:text-red-400 text-lg mb-2">
                  Error
                </div>
                <p className="text-gray-600 dark:text-gray-400">
                  {rawData?.error || "Failed to load raw data"}
                </p>
              </div>
            </div>
          ) : !hasAnySectionData ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <p className="text-gray-600 dark:text-gray-400">
                  No data available
                </p>
              </div>
            </div>
          ) : dataArray.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <p className="text-gray-600 dark:text-gray-400">
                  No transactions on this date
                </p>
              </div>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="flex items-center justify-center py-12">
                <div className="text-center">
                <p className="text-gray-600 dark:text-gray-400">
                  No rows match the filter. Try a different search.
                </p>
              </div>
            </div>
          ) : (
            <>
              {headerData.length > 0 && (
                <div className="px-6 py-3 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Summary
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Object.entries(headerData[0] || {}).map(([key, value]) => (
                      <div key={key} className="text-sm">
                        <span className="text-gray-500 dark:text-gray-400">
                          {key.replace(/_/g, " ")}:{" "}
                        </span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                          {String(value)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex-1 overflow-auto px-6 py-4">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-900/50 sticky top-0">
                      <tr>
                        {visibleColumns.map((column) => (
                          <th
                            key={column}
                            className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                          >
                            {column.replace(/_/g, " ")}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {filteredData.map(
                        (row: Record<string, any>, index: number) => (
                          <tr
                            key={index}
                            className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                          >
                            {visibleColumns.map((column) => {
                              const value = row[column];
                              const display =
                                value !== null && value !== undefined
                                  ? String(value)
                                  : "-";
                              const drillId = activeDrilldownRule
                                ? resolveDrilldownApiValue(
                                    row,
                                    value,
                                    activeDrilldownRule
                                  )
                                : null;
                              const columnNorm = normalizeColumnLabel(column);
                              const clickableDrilldown =
                                summaryDrilldownActive &&
                                activeDrilldownRule &&
                                activeDrilldownRule.clickColumnLabels.includes(
                                  columnNorm
                                ) &&
                                drillId !== null &&
                                display !== "-";

                              if (clickableDrilldown && activeDrilldownRule) {
                                return (
                                  <td
                                    key={column}
                                    className="px-4 py-3 text-sm whitespace-nowrap"
                                  >
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleDrilldownCellClick(
                                          row,
                                          value,
                                          activeDrilldownRule
                                        )
                                      }
                                      className="text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 underline font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded"
                                      title={activeDrilldownRule.linkTitle}
                                    >
                                      {display}
                                    </button>
                                  </td>
                                );
                              }

                              return (
                                <td
                                  key={column}
                                  className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap"
                                >
                                  {display}
                                </td>
                              );
                            })}
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 flex-shrink-0">
          <div className="flex items-center justify-end gap-3 flex-shrink-0">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
            >
              Close
            </button>
            {hasAnySectionData && (
              <>
                <button
                  onClick={handleDownloadExcel}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors flex items-center gap-2"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  Download Excel
                </button>
                {/* <button
                onClick={handleDownloadJson}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors flex items-center gap-2"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                Download JSON
              </button> */}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
